import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { downloadCotizacionPdf, printCotizacionPdf } from "@/lib/pdf";

const LOGO = "/logo-cooper.png";

export default function GuiaInstalacion() {
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const handleDownload = () => downloadCotizacionPdf(bodyRef.current, headerRef.current, "Guia de instalacion - Cooper Slip");
  const handlePrint = () => printCotizacionPdf(bodyRef.current, headerRef.current);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="guia-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/configuracion")}><ArrowLeft className="h-4 w-4 mr-2" /> Configuración</Button>
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Guía de instalación</h1>
          <p className="text-muted-foreground mt-2">Instrucciones para instalar el CRM en celular y PC (Google Chrome).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDownload} data-testid="guia-descargar"><Download className="h-4 w-4 mr-2" /> Descargar PDF</Button>
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="guia-imprimir"><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
        </div>
      </div>

      <div className="bg-gray-200 rounded-md p-4 sm:p-8 overflow-x-auto flex justify-center" data-testid="guia-preview">
        <div className="bg-white shadow" style={{ width: "816px", minHeight: "1056px" }}>
          <div ref={headerRef}>
            <div className="px-8 pt-6">
              <div className="grid grid-cols-[auto_1fr_auto] gap-6 items-start bg-gray-50 px-3 py-3">
                <div className="text-center">
                  <img src={LOGO} className="h-16 w-16 object-contain rounded-lg mx-auto" alt="Cooper Slip" />
                  <div className="text-xs mt-1">Cooper Slip</div>
                </div>
                <div className="text-center text-sm font-bold leading-snug pt-3">
                  <p>Cuando tu cliente queda bien, Tu quedas mejor.</p>
                  <p>Elije las duelas que respaldan a tu negocio</p>
                </div>
                <div className="text-right">
                  <h2 className="text-2xl font-extrabold tracking-tight leading-tight">GUÍA DE<br />INSTALACIÓN</h2>
                  <p className="text-sm mt-1"><span className="font-bold">Sistema:</span> CRM Cooper Slip</p>
                </div>
              </div>
              <div className="border-t-4 border-black" />
            </div>
          </div>

          <div ref={bodyRef} className="px-8 py-6 text-sm leading-relaxed text-gray-900">
            <p className="mb-4">Con esta guía podrás tener el CRM de <b>Duelas Cooper Slip</b> siempre a la mano: como una aplicación en tu celular y como acceso directo en tu computadora. Te recomendamos usar el navegador <b>Google Chrome</b>.</p>
            <p className="mb-6"><b>Sitio web:</b> <a href="https://www.duelascooper.com" target="_blank" rel="noopener noreferrer" className="font-mono text-blue-600 underline">www.duelascooper.com</a> &nbsp;→&nbsp; sección <b>Vendedores</b> &nbsp;→&nbsp; <b>Acceso a vendedores</b></p>

            <h3 className="text-base font-bold mb-2">En tu celular (Android o iPhone)</h3>
            <ol className="list-decimal ml-6 space-y-1 mb-6">
              <li>Abre <b>Google Chrome</b>, entra a <a href="https://www.duelascooper.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-semibold">www.duelascooper.com</a>, ve a la sección <b>Vendedores</b> y luego a <b>Acceso a vendedores</b>.</li>
              <li>Inicia sesión (o quédate en la pantalla de inicio de sesión).</li>
              <li>Toca el menú de los <b>tres puntos</b> (arriba a la derecha).</li>
              <li>Elige <b>“Agregar a pantalla principal”</b> (o “Instalar aplicación”).</li>
              <li>Confirma con <b>“Agregar”</b> o <b>“Instalar”</b>.</li>
              <li>Listo: aparecerá el ícono rojo de <b>Cooper Slip</b> en la pantalla de tu teléfono, igual que cualquier otra app.</li>
            </ol>

            <h3 className="text-base font-bold mb-2">En tu computadora (PC con Google Chrome)</h3>
            <p className="mb-1 font-semibold">A) Agregar a la barra de marcadores</p>
            <ol className="list-decimal ml-6 space-y-1 mb-3">
              <li>Abre <b>Google Chrome</b>, entra a <a href="https://www.duelascooper.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-semibold">www.duelascooper.com</a>, ve a la sección <b>Vendedores</b> y luego a <b>Acceso a vendedores</b>.</li>
              <li>A la derecha de la barra de direcciones, haz clic en la <b>estrella</b> (☆).</li>
              <li>En “Carpeta”, elige <b>“Barra de marcadores”</b> y haz clic en <b>Listo</b>.</li>
            </ol>
            <p className="mb-1 font-semibold">B) ¿Dónde queda y cómo mostrar la barra de marcadores?</p>
            <ol className="list-decimal ml-6 space-y-1 mb-3">
              <li>La <b>barra de marcadores</b> aparece <b>justo debajo de la barra de direcciones</b>, en la parte superior de la ventana.</li>
              <li>Si no la ves, muéstrala con <b>Ctrl + Mayús + B</b> (o menú de tres puntos → Marcadores → Mostrar barra de marcadores).</li>
              <li>Tu acceso a <b>Cooper Slip</b> quedará visible ahí para abrirlo con un solo clic.</li>
            </ol>
            <p className="mb-6 text-gray-600 text-xs">Sugerencia: en la PC también puedes instalarla como aplicación con el ícono <b>“Instalar”</b> que aparece a la derecha de la barra de direcciones; se abrirá en su propia ventana, sin pestañas.</p>

            <div className="mt-10 text-sm" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <p className="font-semibold">Atentamente,</p>
              <p>Departamento de Sistemas</p>
              <p className="text-xs text-gray-600">Duelas Cooper Slip</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
